import React, { useEffect, useState } from 'react';
import Modal from '../../../BaseComponents/Modal';
import Button from '../../../BaseComponents/Button';
import { toast } from 'react-toastify';
import { ReactComponent as ClockIcon } from '../../../../assets/icons/ClockCircle.svg';
import { viewGpSnapshotSummary, GpSnapshotResponse } from '../../../../api/chat';
import { getMemberGpSummary, MemberGpSummaryResponse } from '../../../../api/members';
import AiMarkdownContent from '../AiMarkdownContent';
import './MemberGPSummaryPopup.scss';

type MemberGPSummaryPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName?: string;
};

const hasCachedGpSummary = (
  data: MemberGpSummaryResponse | null
): data is MemberGpSummaryResponse & { reply: string } => {
  if (!data || data.success === false) return false;
  return typeof data.reply === 'string' && data.reply.trim().length > 0;
};

const applyGpSummaryResponse = (
  data: GpSnapshotResponse,
  setReportText: (text: string) => void,
  setErrorText: (text: string) => void
): boolean => {
  if (data?.success === false) {
    setErrorText(data.reply || 'GP summary cannot be generated with the available data.');
    return false;
  }

  if (!data?.reply || typeof data.reply !== 'string' || data.reply.trim().length === 0) {
    setErrorText('GP summary was generated, but no report text was returned.');
    return false;
  }

  setReportText(data.reply.trim());
  return true;
};

const formatPerformedAt = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const MemberGPSummaryPopup = ({
  isOpen,
  onClose,
  memberId,
  memberName,
}: MemberGPSummaryPopupProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [reportText, setReportText] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const [performedAt, setPerformedAt] = useState<string>('');
  const [isFromCache, setIsFromCache] = useState(false);

  const generateReport = async () => {
    if (!memberId) return false;

    const data: GpSnapshotResponse = await viewGpSnapshotSummary(memberId);
    setIsFromCache(false);
    setPerformedAt('');
    return applyGpSummaryResponse(data, setReportText, setErrorText);
  };

  const loadReport = async () => {
    if (!memberId) return;
    setIsLoading(true);
    setErrorText('');
    setReportText('');
    setPerformedAt('');
    setIsFromCache(false);

    try {
      const cached = await getMemberGpSummary(memberId);

      if (hasCachedGpSummary(cached)) {
        setReportText(cached.reply.trim());
        setIsFromCache(true);
        if (cached.performed_at) {
          setPerformedAt(cached.performed_at);
        }
        return;
      }

      await generateReport();
    } catch (err: unknown) {
      let errorMessage = 'Something went wrong while loading GP summary.';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
      setErrorText(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!memberId || isRegenerating) return;
    setIsRegenerating(true);
    setErrorText('');

    try {
      await generateReport();
    } catch (err: unknown) {
      let errorMessage = 'Something went wrong while generating GP summary.';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
      setErrorText(errorMessage);
    } finally {
      setIsRegenerating(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    void loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, memberId]);

  const showRegenerate = !isLoading && !errorText && reportText;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="900px"
      memberName={memberName}
      titleAction={
        <div className="gp-summary-header">
          <span className="gp-summary-header-title">GP summary</span>
          {showRegenerate && (
            <Button
              text="Regenerate"
              onClick={() => void handleRegenerate()}
              isSecondary={true}
              variant="refresh"
              isLoading={isRegenerating}
              isDisabled={isRegenerating}
              height="32px"
              paddingTop="6px"
              paddingBottom="6px"
              paddingLeft="12px"
              paddingRight="12px"
              borderRadius="10px"
            />
          )}
        </div>
      }
    >
      <div className="gp-summary-content">
        {isLoading && <div className="gp-summary-loading">Loading GP summary...</div>}

        {isRegenerating && (
          <div className="gp-summary-loading">Regenerating GP summary...</div>
        )}

        {!isLoading && !isRegenerating && errorText && (
          <div className="gp-summary-error">{errorText}</div>
        )}

        {!isLoading && !errorText && reportText && (
          <>
            {isFromCache && performedAt && (
              <div className="gp-summary-meta">
                <ClockIcon className="gp-summary-meta-icon" aria-hidden />
                <div className="gp-summary-meta-text">
                  <span className="gp-summary-meta-label">Last generated</span>
                  <span className="gp-summary-meta-value">{formatPerformedAt(performedAt)}</span>
                </div>
              </div>
            )}
            <AiMarkdownContent content={reportText} className="gp-summary-report" variant="gp-summary" />
          </>
        )}

        {!isLoading && !isRegenerating && !errorText && !reportText && (
          <div className="gp-summary-loading" style={{ opacity: 0.6 }}>
            Click "View GP Summary" to generate a report.
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MemberGPSummaryPopup;
